import React, { type ReactNode, useCallback } from 'react';

import { useCrossProductUrlWrapper } from '@atlassiansox/analytics-cross-product-interaction-client';
import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { type CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap, cx } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { ButtonMenuItem } from '@atlaskit/navigation-system/side-nav-items/button-menu-item';
import { LinkMenuItem as MenuLinkItemPrimitive } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { L1_MENU_GROUPS } from '@atlassian/navigation-common';
import { useL1ItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	GlobalShortcutActionsItem,
	GlobalShortcutConfiguration,
	GlobalShortcutLinkItem,
} from '../../types';
import { ElementEventListener } from '../../utils/element-event-listener';
import { useDragAndDropOptions } from '../../utils/use-drag-and-drop-options';
import { MenuLoadingItem } from '../menu-loading-item';
import { ShortcutActionMenu } from '../shortcut-action';

import { messages } from './messages';

export type MenuLinkItemProps = Omit<GlobalShortcutLinkItem, 'ref' | 'type'> & {
	actions?: GlobalShortcutActionsItem[];
	ariaLabel?: string;
	children?: ReactNode;
	disabled?: boolean;
	isReorderable?: boolean;
	loading?: boolean;
	onDisable?: (reason?: string) => void;
	openInSameTab?: boolean;
	optionalRef?: GlobalShortcutConfiguration['ref'];
	product?: string;
	url?: string;
};

export type MenuLinkItemDraggableProps = Omit<MenuLinkItemProps, 'menuItemId'> & {
	actions?: GlobalShortcutActionsItem[];
	ariaLabel?: string;
	isReorderable?: boolean;
	menuItemId: string;
	url: string;
};

export const fireMenuLinkItemClickAnalyticsEvent = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	analyticsName?: string,
) => {
	createAnalyticsEvent({
		eventType: UI_EVENT_TYPE,
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'globalNavigation',
		attributes: {
			name: analyticsName,
		},
	}).fire(ANALYTICS_CHANNEL);
};

const styles = cssMap({
	iconsBlock: {
		paddingInline: token('space.075'),
	},
});

const MenuLinkItemComponent = ({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	optionalRef,
	openInSameTab,
	analyticsName,
}: MenuLinkItemProps) => {
	const { formatMessage } = useIntl();
	const mergedRefs = mergeRefs(optionalRef ? [optionalRef] : []);

	// Only render link item if url is defined
	return (
		<MenuLinkItemPrimitive
			actionsOnHover={
				actions &&
				actions.length && <ShortcutActionMenu actions={actions} analyticsName={analyticsName} />
			}
			elemBefore={icon}
			// TODO: Add spacing="spacious" to LinkExternalIcon, and we can remove the parent Box
			elemAfter={
				openInSameTab ? undefined : (
					<Box xcss={cx(styles.iconsBlock)}>
						<LinkExternalIcon label={formatMessage(messages.linkExternalIconLabel)} size="small" />
					</Box>
				)
			}
			href={url || ''}
			target={openInSameTab ? '_self' : '_blank'}
			onClick={onClick}
			ref={mergedRefs}
		>
			<span aria-label={ariaLabel}>{labelElem ?? label}</span>
		</MenuLinkItemPrimitive>
	);
};

const MenuLinkItemDraggableComponent = ({
	url,
	label,
	labelElem,
	icon,
	ariaLabel,
	actions,
	onClick,
	isReorderable,
	optionalRef,
	menuItemId,
	openInSameTab,
	analyticsName,
}: MenuLinkItemDraggableProps) => {
	const { formatMessage } = useIntl();
	const { enabled } = useDragAndDropOptions();
	const { draggableRef, isDragging, maybeDragPreview, dropTargetRef, dropIndicator } =
		useL1ItemDragAndDrop({
			id: menuItemId,
			isExpandable: false,
			isExpandOrFlyoutOpen: false,
			l1DragAndDropEnabled: enabled,
			onExpandFlyoutToggle: () => {},
			peek: false,
			item: {
				type: 'link',
				elemBefore: icon,
				label,
				labelElem,
				href: url || '',
				isReorderable,
			},
			group: L1_MENU_GROUPS.SHORTCUTS,
		});

	// Can possibly be undefined
	const draggableRefCollection = draggableRef ? [draggableRef] : [];
	const mergedRefs = mergeRefs(
		optionalRef ? [optionalRef, ...draggableRefCollection] : [...draggableRefCollection],
	);

	// Only render link item if url is defined
	return (
		<>
			<MenuLinkItemPrimitive
				isDragging={isDragging}
				hasDragIndicator
				ref={mergedRefs}
				dropIndicator={dropIndicator}
				visualContentRef={dropTargetRef}
				actionsOnHover={
					actions &&
					actions.length && <ShortcutActionMenu actions={actions} analyticsName={analyticsName} />
				}
				elemBefore={icon}
				elemAfter={
					openInSameTab ? undefined : (
						<Box xcss={cx(styles.iconsBlock)}>
							<LinkExternalIcon
								label={formatMessage(messages.linkExternalIconLabel)}
								size="small"
							/>
						</Box>
					)
				}
				href={url || ''}
				target={openInSameTab ? '_self' : '_blank'}
				onClick={onClick}
			>
				<span aria-label={ariaLabel}>{labelElem ?? label}</span>
			</MenuLinkItemPrimitive>
			{maybeDragPreview}
		</>
	);
};

export const MenuLinkItem = ({
	analyticsName,
	url,
	label,
	labelElem,
	icon,
	disabled,
	onDisable,
	ariaLabel,
	loading,
	actions,
	onClick,
	onRightClick,
	onFocus,
	onMouseEnter,
	optionalRef,
	isReorderable,
	menuItemId,
	product = 'globalNavigation',
	openInSameTab,
}: MenuLinkItemProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { enabled } = useDragAndDropOptions();

	const analyticsUrlWrapper = useCrossProductUrlWrapper({
		bridge: 'globalShortcuts',
		product,
	});

	const getTargetUrl = useCallback(
		(url: string) => {
			// For SPA links, we don't want to wrap the URL
			if (!isValidUrl(url)) {
				return url;
			}

			try {
				const parsedUrl = new URL(url);
				const continueParam = parsedUrl.searchParams.get('continue');
				if (!continueParam) {
					// If there is no continue param, we just wrap the whole URL
					return analyticsUrlWrapper(url);
				}

				// Safely deserialize identity links e.g: https://id.atlassian.com/login?login_hint=...
				const wrappedContinueUrl = analyticsUrlWrapper(continueParam);
				// Reconstruct the original URL with the wrapped continue param
				parsedUrl.searchParams.set('continue', wrappedContinueUrl);
				return parsedUrl.toString();
			} catch (e) {
				// Gracefully handle any URL parsing errors by returning the original URL
				// TODO: Log error to Sentry
				return url;
			}
		},
		[analyticsUrlWrapper],
	);

	const onClickMenuLinkItem = useCallback(() => {
		fireMenuLinkItemClickAnalyticsEvent(createAnalyticsEvent, analyticsName);
		onClick?.();
	}, [createAnalyticsEvent, analyticsName, onClick]);

	// If loading is true, we want to render a representation of the button
	if (loading === true) {
		return <MenuLoadingItem icon={icon} label={labelElem ?? label} showLinkIcon />;
	}

	// Don't render anything if url is undefined or empty, and disabled is not explicitly set to true
	if ((!url || url === '') && !disabled) {
		return null;
	}

	// Render disabled items if no url and/or disabled is true
	if (!url || disabled) {
		if (!url || url === '') {
			onDisable?.('Invalid URL');
		} else {
			onDisable?.();
		}

		return (
			<ButtonMenuItem elemBefore={icon} isDisabled>
				{labelElem ?? label}
				<span aria-label={formatMessage(messages.disabledItemAriaLabel)} />
			</ButtonMenuItem>
		);
	}

	const targetUrl = getTargetUrl(url);

	if (fg('teams-shortcut-next-confluence')) {
		return (
			<ElementEventListener
				onRightClick={onRightClick}
				onMouseEnter={onMouseEnter}
				onFocus={onFocus}
			>
				{enabled && menuItemId ? (
					<MenuLinkItemDraggableComponent
						optionalRef={optionalRef}
						url={targetUrl}
						icon={icon}
						ariaLabel={ariaLabel}
						label={label}
						labelElem={labelElem}
						actions={actions}
						onClick={onClickMenuLinkItem}
						isReorderable={isReorderable}
						menuItemId={menuItemId}
						openInSameTab={openInSameTab}
					/>
				) : (
					<MenuLinkItemComponent
						optionalRef={optionalRef}
						url={targetUrl}
						icon={icon}
						ariaLabel={ariaLabel}
						label={label}
						labelElem={labelElem}
						actions={actions}
						onClick={onClickMenuLinkItem}
						isReorderable={isReorderable}
						openInSameTab={openInSameTab}
					/>
				)}
			</ElementEventListener>
		);
	}

	return (
		<ElementEventListener handler={onRightClick}>
			{enabled && menuItemId ? (
				<MenuLinkItemDraggableComponent
					optionalRef={optionalRef}
					url={targetUrl}
					icon={icon}
					ariaLabel={ariaLabel}
					label={label}
					labelElem={labelElem}
					actions={actions}
					onClick={onClickMenuLinkItem}
					isReorderable={isReorderable}
					menuItemId={menuItemId}
					openInSameTab={openInSameTab}
					analyticsName={analyticsName}
				/>
			) : (
				<MenuLinkItemComponent
					optionalRef={optionalRef}
					url={targetUrl}
					icon={icon}
					ariaLabel={ariaLabel}
					label={label}
					labelElem={labelElem}
					actions={actions}
					onClick={onClickMenuLinkItem}
					isReorderable={isReorderable}
					openInSameTab={openInSameTab}
					analyticsName={analyticsName}
				/>
			)}
		</ElementEventListener>
	);
};

const isValidUrl = (urlString: unknown) => {
	if (typeof urlString !== 'string') {
		return false;
	}
	try {
		return Boolean(new URL(urlString));
	} catch (e) {
		return false;
	}
};
